import { useId, type ReactElement } from 'react'
import { Box, ButtonBase, Chip, IconButton, Stack, Tooltip, Typography, type ChipProps } from '@mui/material'
import { ArrowDownward, ArrowUpward, CheckCircleOutline, DeleteOutline, ErrorOutline, EventBusy, ExpandMore, Schedule, VisibilityOff } from '@mui/icons-material'
import { referenciaBanner, resumoPeriodo, type BannerRascunho, type SituacaoBanner } from '../../lib/banners'

interface ResumoBannerProps {
  banner: BannerRascunho
  numero: number
  total: number
  /** "Abre: …", "Só a imagem" ou o que falta escolher. */
  textoDestino: string
  /** `null` quando ainda não dá para saber (catálogo carregando): a etiqueta não aparece. */
  situacao: SituacaoBanner | null
  hoje: string
  aberto: boolean
  idPainel: string
  aoAlternar: () => void
  aoMover: (deslocamento: -1 | 1) => void
  aoRemover: () => void
}

// O ícone repete a cor em forma: quem não distingue as cores ainda lê a etiqueta.
const ETIQUETA: Record<SituacaoBanner, { rotulo: string; cor: ChipProps['color']; icone: ReactElement }> = {
  FALTA_COMPLETAR: { rotulo: 'Falta completar', cor: 'error', icone: <ErrorOutline /> },
  NAO_APARECE: { rotulo: 'Não aparece', cor: 'warning', icone: <VisibilityOff /> },
  ENCERRADO: { rotulo: 'Encerrado', cor: 'default', icone: <EventBusy /> },
  AGENDADO: { rotulo: 'Agendado', cor: 'info', icone: <Schedule /> },
  NO_AR: { rotulo: 'No ar', cor: 'success', icone: <CheckCircleOutline /> },
}

/** Linha fechada do banner. No celular é grade (imagem | texto e etiqueta; botões embaixo); do `sm` para cima, os grupos `contents` voltam a ser flex. */
export function ResumoBanner({
  banner,
  numero,
  total,
  textoDestino,
  situacao,
  hoje,
  aberto,
  idPainel,
  aoAlternar,
  aoMover,
  aoRemover,
}: ResumoBannerProps): JSX.Element {
  const id = useId()
  const titulo = banner.nome?.trim() || `Banner ${numero}`
  const referencia = referenciaBanner(banner, numero)
  const etiqueta = situacao ? ETIQUETA[situacao] : null

  return (
    <Box
      sx={{
        position: 'relative',
        display: { xs: 'grid', sm: 'flex' },
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        gridTemplateAreas: '"imagem texto" "imagem etiqueta" "acoes acoes"',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: 1.5,
        rowGap: 0.5,
        px: 1.5,
        py: 1.5,
      }}
    >
      {/* Botão esticado sobre a linha toda: o clique em qualquer ponto abre, menos nas setas e na lixeira, que ficam por cima. */}
      <ButtonBase
        focusRipple
        onClick={aoAlternar}
        aria-expanded={aberto}
        aria-controls={idPainel}
        aria-labelledby={`${id}-titulo`}
        aria-describedby={etiqueta ? `${id}-detalhe ${id}-situacao` : `${id}-detalhe`}
        sx={(tema) => ({
          position: 'absolute',
          inset: 0,
          '&:hover': { bgcolor: 'action.hover' },
          '&.Mui-focusVisible': { outline: `2px solid ${tema.palette.primary.main}`, outlineOffset: -2 },
        })}
      />

      <Box
        sx={(tema) => ({
          display: { xs: 'contents', sm: 'flex' },
          alignItems: 'center',
          gap: 1.5,
          flex: `1 1 ${tema.spacing(30)}`,
          minWidth: 0,
        })}
      >
        <Box
          component="img"
          src={banner.urlImagem}
          alt=""
          draggable={false}
          sx={{
            gridArea: 'imagem',
            alignSelf: 'start',
            width: 96,
            height: 48,
            flexShrink: 0,
            objectFit: 'cover',
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        />
        <Box sx={{ gridArea: 'texto', minWidth: 0 }}>
          <Typography id={`${id}-titulo`} variant="subtitle2" noWrap>
            {titulo}
          </Typography>
          <Typography id={`${id}-detalhe`} variant="body2" color="text.secondary">
            {`${textoDestino} · ${resumoPeriodo(banner, hoje)}`}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: { xs: 'contents', sm: 'flex' }, alignItems: 'center', gap: 0.5, ml: 'auto' }}>
        {etiqueta && (
          <Chip
            id={`${id}-situacao`}
            size="small"
            color={etiqueta.cor}
            icon={etiqueta.icone}
            label={etiqueta.rotulo}
            sx={{ gridArea: 'etiqueta', justifySelf: 'start' }}
          />
        )}

        <Box sx={{ gridArea: 'acoes', justifySelf: 'end', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Acima do botão esticado: o clique numa seta (mesmo desabilitada) não abre nem fecha o painel. */}
          <Stack direction="row" spacing={0.5} useFlexGap sx={{ position: 'relative', zIndex: 1 }}>
            <Tooltip title="Mover para cima">
              <span>
                <IconButton aria-label={`Mover banner ${referencia} para cima`} disabled={numero === 1} onClick={() => aoMover(-1)}>
                  <ArrowUpward />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Mover para baixo">
              <span>
                <IconButton
                  aria-label={`Mover banner ${referencia} para baixo`}
                  disabled={numero === total}
                  onClick={() => aoMover(1)}
                >
                  <ArrowDownward />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Remover banner">
              <IconButton color="error" aria-label={`Remover banner ${referencia}`} onClick={aoRemover} sx={{ ml: 2 }}>
                <DeleteOutline />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Girada, a seta ganha `transform` e passa a ser desenhada por cima do botão esticado: sem `pointerEvents`, ela engolia o clique de fechar. */}
          <ExpandMore
            aria-hidden
            sx={(tema) => ({
              pointerEvents: 'none',
              flexShrink: 0,
              color: 'action.active',
              transform: aberto ? 'rotate(180deg)' : 'none',
              transition: tema.transitions.create('transform'),
            })}
          />
        </Box>
      </Box>
    </Box>
  )
}
